import {
  AssignmentTurnedInOutlined,
  AttachFileOutlined,
  CreditScoreOutlined,
  EventNoteOutlined,
  FactCheckOutlined,
  FolderOpenOutlined,
  PeopleOutline,
  PersonAddOutlined,
  PersonOffOutlined,
} from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { grey } from "@mui/material/colors";
import { isEmpty, size } from "lodash";
import { memo, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import KpiWidgetGroup, {
  progressTone,
} from "../../../../components/data/KpiWidgetGroup";
import Flx from "../../../../components/layout/Flx";
import Htag from "../../../../components/typography/Htag";
import Txt from "../../../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { sidebarTypeToggle } from "../../../../redux/actions/sidebarActions";
import {
  DETAIL_SIDEBAR_CONTENT_TYPES,
  detailSidebarOpen,
} from "../../../../redux/reducers/detailSidebarReducer";
import LoanDocumentManagerTable from "../LoanDrilldownDocumentsTab/LoanDocumentManagerTable";
import LoanTabShell from "../../shared/tabLayout/LoanTabShell";
import ApplicantCard from "./components/ApplicantCard";
import { useApplicantHistory } from "../../applicantHistory/useApplicantHistory";
import ExperienceItemDialog from "../../experience/ExperienceItemDialog";
import { partiesById } from "../../experience/experienceConstants";
import { useExperienceWorkbook } from "../../experience/useExperienceWorkbook";
import EmptyStatePlaceholder from "../../../../components/feedback/EmptyStatePlaceholder";
import { UploadQueueProvider } from "../LoanDrilldownDocumentsTab/UploadQueueContext";
import { FixedUploadQueue } from "../LoanDrilldownDocumentsTab/LoanDrilldownDocumentsTab";

// Doc groups used for borrower-related documents — matches
// `LoanBorrowerFileUploadProcess.allowedDocGroups`. Drives the files table
// at the bottom of the tab.
const BORROWER_DOC_GROUPS = [
  "Borrower Documents",
  "Borrower Reports",
  "Experience and Track Record",
  "Liquidity",
  "Related Entity Docs",
];

const LoanDrilldownApplicantsTab = () => {
  const dispatch = useDispatch();

  const rawBorrowers = useSelector(
    (state) => state?.loanDrilldown?.borrowers || [],
  );
  // Primary contact(s) first, everyone else in their stored order. A stable
  // sort, so two primaries (or none) keep their relative positions.
  const borrowers = useMemo(
    () =>
      [...rawBorrowers].sort(
        (a, b) =>
          Number(Boolean(b?.isPrimaryContactForLoan)) -
          Number(Boolean(a?.isPrimaryContactForLoan)),
      ),
    [rawBorrowers],
  );
  const loanDocuments = useSelector(
    (state) => state?.loanDrilldown?.loanDocuments || [],
  );
  const detailSidebar = useSelector((state) => state.detailSidebar);
  const dealId = useSelector((state) => state?.loanDrilldown?._id);

  // Applicant history (prior EV / LQ loans + FCI status) — ONE fetch for the
  // loan, sliced per card by borrowerId. Re-asked when the applicants change
  // (the backend recomputes when its stored fingerprint no longer matches).
  const borrowersKey = useMemo(
    () =>
      rawBorrowers
        .map((b) =>
          [b?._id, b?.firstName, b?.lastName, b?.emailAddress].join("|"),
        )
        .join(";"),
    [rawBorrowers],
  );
  const history = useApplicantHistory(dealId, borrowersKey);
  const priorLoansByBorrower = useMemo(() => {
    if (history.unavailable) return {};
    const byId = {};
    for (const a of history.data?.applicants || []) {
      if (a?.borrowerId) byId[String(a.borrowerId)] = a;
    }
    const out = {};
    for (const b of rawBorrowers) {
      out[String(b?._id)] = {
        applicant: byId[String(b?._id)] || null,
        payload: history.data,
        loading: history.loading,
        refreshing: history.refreshing,
        error: history.error,
        onRefresh: history.refresh,
      };
    }
    return out;
  }, [history, rawBorrowers]);

  // Credited experience — the experience items tied to each applicant, like
  // the General Contractors roster shows per contractor. One workbook for the
  // loan (shared with the Experience tab through Redux), sliced by borrower.
  const loan = useSelector((state) => state?.loanDrilldown);
  const wb = useExperienceWorkbook(dealId);
  const experienceApplies = wb.experienceApplies !== false;
  const [experienceItem, setExperienceItem] = useState(null);
  const partyById = useMemo(() => partiesById(loan), [loan]);
  const itemsByBorrower = useMemo(() => {
    const map = new Map();
    if (!experienceApplies) return map;
    (wb.items || []).forEach((it) =>
      (it?.associations?.borrowers || []).forEach((bid) => {
        const key = String(bid);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(it);
      }),
    );
    return map;
  }, [wb.items, experienceApplies]);

  // Useful for highlighting the active card:
  const selectedId = detailSidebar.entityId;

  const guarantorCount = useMemo(
    () => size(borrowers.filter((b) => b?.isGuarantor)),
    [borrowers],
  );

  // Per-key-date completion counts. Each tile reads "X of Y" so the
  // operator can see at a glance how many borrowers are missing a given
  // date stamp. A borrower is considered "entered" if the field is a
  // non-empty value (string, Date, etc.).
  const dateProgress = useMemo(() => {
    const total = borrowers.length;
    const has = (key) =>
      borrowers.filter((b) => {
        const v = b?.[key];
        return v != null && v !== "";
      }).length;
    return {
      total,
      background: has("backgroundDate"),
      creditAuth: has("creditAuthDate"),
      creditReport: has("creditReportDate"),
    };
  }, [borrowers]);

  // Borrower-related documents — flat list, the table groups by docGroup
  // internally via `forcedLayout="docGroup"`.
  const borrowerDocs = useMemo(
    () =>
      (loanDocuments || []).filter(
        (d) => d?.docGroup && BORROWER_DOC_GROUPS.includes(d.docGroup),
      ),
    [loanDocuments],
  );

  const handleSelectBorrower = useCallback(
    (borrower) => {
      dispatch(
        detailSidebarOpen(
          DETAIL_SIDEBAR_CONTENT_TYPES.BORROWER,
          borrower,
          borrower?._id,
          {
            title:
              `${borrower?.firstName || ""} ${borrower?.lastName || ""}`.trim() ||
              "Borrower",
            mode: "view",
            source: "LoanDrilldownApplicantsTab",
            sourceField: "borrowers",
          },
        ),
      );
    },
    [dispatch],
  );

  if (isEmpty(borrowers)) {
    return (
      <Box>
        <EmptyStatePlaceholder
          icon={<PersonOffOutlined />}
          title="No applicants on this loan"
          description="Add applicants to this loan to track their information and progress."
          actions={
            <Flx ac g={1}>
              <AddBorrowerButton />
            </Flx>
          }
        />
      </Box>
    );
  }

  return (
    // Files dropped on any applicant card's documents rail stage here and are
    // reviewed / committed from the bottom-docked FixedUploadQueue, exactly
    // like the Documents and Entity tabs.
    <UploadQueueProvider>
      <LoanTabShell
        title="Applicants"
        subTitle="Guarantors and borrowers featured this loan."
        primaryAction={{
          label: "Add applicant",
          icon: <PersonAddOutlined />,
          onClick: () =>
            dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.ADD_BORROWER)),
        }}
        bottomContent={
          borrowers.length > 0 ? (
            <ApplicantsSummaryBar
              borrowerCount={borrowers.length}
              guarantorCount={guarantorCount}
              dateProgress={dateProgress}
            />
          ) : undefined
        }
      >
        <Flx column>
          <Flx column sx={{ mb: 1.5 }}>
            <Htag h3>All Loan Applicants</Htag>
            <Txt secondary sx={{ fontSize: 12 }}>
              Click any applicant to view or edit details.
            </Txt>
          </Flx>
          {size(borrowers) === 0 ? (
            <Flx
              center
              column
              sx={{
                py: 8,
                color: grey[400],
                gap: 1,
              }}
            >
              <PersonAddOutlined sx={{ fontSize: 40 }} />
              <Box sx={{ fontSize: 14, fontWeight: 500 }}>
                No borrowers on this loan yet
              </Box>
            </Flx>
          ) : (
            <Flx column gap={2}>
              {borrowers.map((borrower) => (
                <ApplicantCard
                  key={borrower._id}
                  borrower={borrower}
                  isSelected={selectedId === borrower._id}
                  priorLoans={priorLoansByBorrower[String(borrower._id)]}
                  experience={{
                    items: itemsByBorrower.get(String(borrower._id)) || [],
                    loan,
                    partyById,
                    activeItemId: experienceItem?._id,
                    onOpenItem: setExperienceItem,
                  }}
                  onClick={() => handleSelectBorrower(borrower)}
                />
              ))}
            </Flx>
          )}
        </Flx>

        {size(borrowers) > 0 && (
          <BorrowerFilesArea
            borrowerDocs={borrowerDocs}
            totalCount={borrowerDocs.length}
          />
        )}
      </LoanTabShell>
      {/* The Experience tab's item editor, opened from any card's
          Credited experience row. */}
      <ExperienceItemDialog
        open={!!experienceItem}
        onClose={() => setExperienceItem(null)}
        item={experienceItem}
        loan={loan}
        busy={wb.busy}
        onCreate={wb.createItem}
        onUpdate={wb.updateItem}
        onVerify={wb.verifyItem}
        onRemove={wb.removeItem}
      />
      <FixedUploadQueue />
    </UploadQueueProvider>
  );
};

/* ─────────────────────────────────────────────
   Summary widget — counts + key-date completeness
   ───────────────────────────────────────────── */

const ApplicantsSummaryBar = memo(
  ({ borrowerCount, guarantorCount, dateProgress }) => {
    if (!borrowerCount && !guarantorCount) return null;
    const items = [
      {
        key: "borrowers",
        label: "Applicants",
        value: borrowerCount,
        icon: <PeopleOutline />,
      },
      {
        key: "guarantors",
        label: "Guarantors",
        value: guarantorCount,
        icon: <FactCheckOutlined />,
      },
      {
        key: "background",
        label: "Background Checks",
        value: `${dateProgress.background} of ${dateProgress.total}`,
        tone: progressTone(dateProgress.background, dateProgress.total),
        icon: <EventNoteOutlined />,
      },
      {
        key: "creditAuth",
        label: "Credit Auths",
        value: `${dateProgress.creditAuth} of ${dateProgress.total}`,
        tone: progressTone(dateProgress.creditAuth, dateProgress.total),
        icon: <AssignmentTurnedInOutlined />,
      },
      {
        key: "creditReport",
        label: "Credit Reports",
        value: `${dateProgress.creditReport} of ${dateProgress.total}`,
        tone: progressTone(dateProgress.creditReport, dateProgress.total),
        icon: <CreditScoreOutlined />,
      },
    ];

    return <KpiWidgetGroup items={items} sx={{ mb: "24px" }} />;
  },
);

/* ─────────────────────────────────────────────
   Borrower Files area — per-doc-group tables
   ─────────────────────────────────────────────
   Mirror of the Subject Properties tab's PropertyFilesArea — re-uses the
   shared LoanDocumentManagerTable with `forcedLayout="docGroup"` so each
   borrower-doc-group renders as its own collapsible section.
*/

const BorrowerFilesArea = memo(({ borrowerDocs, totalCount }) => (
  <Box sx={{ mt: 6 }}>
    <Flx ac jb sx={{ mb: 1.5 }}>
      <Flx column>
        <Htag h3>All Borrower Related Documents</Htag>
        <Txt secondary sx={{ fontSize: 12 }}>
          Documents on this loan in any borrower doc group, grouped by category.
        </Txt>
      </Flx>
      <Flx ac g={0.5}>
        <FolderOpenOutlined sx={{ fontSize: 16, color: grey[500] }} />
        <Txt sx={{ fontSize: 12, color: grey[700], fontWeight: 600 }}>
          {totalCount} total
        </Txt>
      </Flx>
    </Flx>

    <Box
      sx={{
        background: "#fff",
        border: "1px solid #d8e0ea",
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow:
          "0 2px 4px rgba(15, 23, 42, 0.05), 0 6px 16px rgba(15, 23, 42, 0.07)",
      }}
    >
      {totalCount === 0 ? (
        <Flx center column sx={{ py: 6, color: grey[400], gap: 1 }}>
          <AttachFileOutlined sx={{ fontSize: 32 }} />
          <Box sx={{ fontSize: 13 }}>No borrower files yet</Box>
        </Flx>
      ) : (
        <LoanDocumentManagerTable
          rowData={borrowerDocs}
          forcedLayout="docGroup"
          minHeight={"unset"}
          // This card clips overflow, so it is the toolbar's sticky context;
          // the nav-height offset would only open a blank band above it.
          stickyTop="0px"
        />
      )}
    </Box>
  </Box>
));

const AddBorrowerButton = memo(({ size = "medium" }) => {
  const dispatch = useDispatch();
  const handleToggle = () => {
    dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.ADD_BORROWER));
  };
  return (
    <Button
      sx={{ ml: 1 }}
      size={size}
      startIcon={<PersonAddOutlined sx={{ fontSize: "18px !important" }} />}
      onClick={handleToggle}
    >
      Add Applicant
    </Button>
  );
});

export default LoanDrilldownApplicantsTab;
